import { MainMenuItem, MainMenuItemsList } from 'app/navbar/MenuConfig';
import { clearLocalStorage } from 'tools/tools';
import { logout, UserAuth } from 'store/auth';
import { TFunction } from 'i18next';

const userMenuDisplayOptions = {
    HIDE_ALL: 'Hide for all users',
    DISPLAY_ALL: 'Display for all users',
};
export const userMenuItems = (
    userAuth: UserAuth,
    t: TFunction,
    helpLink: string,
    themeMenuSettings: any,
    ignoreThemeSettings?: boolean,
) => {
    const userInfo: any = userAuth?.userInfo ?? {};
    const userBrandThemeCustomLinks = userAuth?.userInfo?.brandThemeProperties?.custom_links ?? [];
    const getVisibilityCondition = (customLink: any) => {
        const showForAdmin = customLink.view_admin != 'N' && userInfo?.isAdmin;
        const showForPower = customLink.view_power != 'N' && userInfo?.isPowerUser;
        const showForRegular = customLink.view_regular != 'N' && !userInfo?.isAdmin && !userInfo?.isPowerUser;

        return [{ type: 'isTrue', params: showForAdmin || showForPower || showForRegular }] as any;
    };

    let userMenuItems: MainMenuItemsList = [];
    if (themeMenuSettings?.user_notifications != 'N' || ignoreThemeSettings) {
        userMenuItems = [
            ...userMenuItems,
            {
                label: 'user.my_notifications',
                href: '/notification/receiving',
                icon: { type: 'mi', value: 'my-notifications' },
                key: 'my_notifications',
                condition: [{ type: 'adminOrPrivilege', params: 'PRIV_PREFERENCES' }],
                searchData: {
                    keywords: {
                        manuLabel: true,
                    },
                    labelSuffix: 'none',
                    groupKey: 'account',
                },
            },
        ];
    }
    if (themeMenuSettings?.user_alerts != 'N' || ignoreThemeSettings) {
        userMenuItems = [
            ...userMenuItems,
            {
                label: 'user.my_alerts',
                href: '/notification/alert-subscription',
                icon: { type: 'mi', value: 'alert' },
                key: 'my_alerts',
                condition: [{ type: 'adminOrPrivilege', params: 'PRIV_PREFERENCES' }],
                searchData: {
                    keywords: {
                        manuLabel: true,
                    },
                    labelSuffix: 'none',
                    groupKey: 'account',
                },
            },
        ];
    }
    if (themeMenuSettings?.user_favorites != 'N' || ignoreThemeSettings) {
        userMenuItems = [
            ...userMenuItems,
            {
                label: 'user.my_favorites',
                href: '/notification/favorite-notification',
                icon: { type: 'mi', value: 'favorite' },
                key: 'my_favorites',
                condition: [{ type: 'adminOrPrivilege', params: 'PRIV_PREFERENCES' }],
                searchData: {
                    desc: 'my_favorites',
                    keywords: {
                        manuLabel: true,
                    },
                    labelSuffix: 'none',
                    groupKey: 'account',
                },
            },
        ];
    }
    if (themeMenuSettings?.user_settings != 'N' || ignoreThemeSettings) {
        userMenuItems = [
            ...userMenuItems,
            {
                label: 'user.my_settings',
                href: '/home/preference',
                icon: { type: 'mi', value: 'my-settings' },
                key: 'my_settings',
                condition: [{ type: 'adminOrPrivilege', params: 'PRIV_PREFERENCES' }],
                searchData: {
                    keywords: {
                        manuLabel: true,
                    },
                    labelSuffix: 'none',
                    groupKey: 'account',
                },
            },
        ];
    }
    if (themeMenuSettings?.user_mobile != 'N' || ignoreThemeSettings) {
        userMenuItems = [
            ...userMenuItems,
            {
                label: 'user.my_mobile',
                href: '/home/preference-mobile',
                icon: { type: 'mi', value: 'mobile' },
                key: 'my_mobile',
                condition: [{ type: 'adminOrPrivilege', params: 'PRIV_PREFERENCES' }],
                searchData: {
                    keywords: {
                        manuLabel: true,
                    },
                    labelSuffix: 'none',
                    groupKey: 'account',
                },
            },
        ];
    }
    if (userMenuItems.length) {
        userMenuItems.push({ divider: true });
    }

    let helpBlockItems: MainMenuItemsList = [];

    [...userBrandThemeCustomLinks]
        .sort((a: any, b: any) => {
            if (a.display_order < b.display_order) return -1;
            if (a.display_order > b.display_order) return 1;
            return 0;
        })
        .forEach((customLink: any) => {
            let link = customLink.url;
            if (link == 'https://help.metricinsights.com' && helpLink) {
                link = helpLink;
            }
            helpBlockItems.push({
                label: customLink.link_text,
                translate: false,
                icon: {
                    type: customLink.icon_ind,
                    value: customLink.icon_ind == 'upload' ? customLink.icon_data : customLink.icon_name,
                },
                key: 'user_help',
                href: link,
                newTab: true,
                isExternal: true,
                condition: getVisibilityCondition(customLink),
                searchData: {
                    keywords: {
                        manuLabel: true,
                    },
                    itemType: 'custom_link',
                    labelSuffix: 'none',
                    groupKey: 'account',
                },
            });
        });

    const aboutItem: MainMenuItem = {
        label: 'user.about',
        icon: { type: 'mi', value: 'info-circle' },
        key: 'about',
        condition: [{ type: 'isAdminOrPowerUser' }],
        children: [
            {
                component: 'MenuAbout',
                label: 'user.about',
                key: 'user_about',
            },
        ],
    };

    if (helpBlockItems.length) {
        if (userInfo?.isAdmin || userInfo?.isPowerUser) {
            helpBlockItems.push(aboutItem);
        }
        helpBlockItems.push({ divider: true });
        userMenuItems = [...userMenuItems, ...helpBlockItems];
    } else {
        if (userInfo?.isAdmin || userInfo?.isPowerUser) {
            userMenuItems.push(aboutItem);
            userMenuItems.push({ divider: true });
        }
    }

    const logoutItem: MainMenuItem = {
        label: userAuth.userInfo?.hijack_username
            ? t('user.return_to_session', { username: userAuth.userInfo?.hijack_username })
            : t('user.log_out'),
        key: 'log_out',
        translate: false,
        icon: { type: 'mi', value: 'log-out' },
        action: ({ dispatch }) => {
            clearLocalStorage();

            if (
                !(undefined !== userAuth.userInfo?.hijack_username && userAuth.userInfo?.hijack_username > '') &&
                userAuth?.userInfo?.isSamlUser
            ) {
                window.location.href = '/auth/saml/logout';
            } else {
                if (dispatch) {
                    dispatch(logout());
                }
            }
        },
        searchData: {
            keywords: {
                custom: [t('user.log_out')],
            },
            itemType: 'custom_link',
            labelSuffix: 'none',
            groupKey: 'account',
        },
    };

    userMenuItems.push(logoutItem);

    return userMenuItems;
};
